import { Dropdown, Icon, Menu, notification, Table } from 'antd';
import { DataSourceItemObject } from "antd/lib/auto-complete";
import { ColumnProps, PaginationConfig } from "antd/lib/table";
import * as React from 'react';
import { NavLink } from 'react-router-dom';

import { getCompanies, getCustomerTypes, ICompany } from "../../apis/Companies";
import ColumnSearch from "../ColumnSearch";
import SimpleColumnSearch from "../SimpleColumnSearch";
import TerminationMenuItem from '../terminations/TerminationMenuItem';
import deletePaymentInstrument from './deletePaymentInstrument';

// As this page expands to hold more information, we can add
// different keys to this object, and only filter on those
// keys down in the `columns` definition
interface IFilteredInfo {
  companyType: string[],
}

interface ILocalState {
  companies: ICompany[],
  companyIDDropdownFilterVisible: boolean,
  companyIDFilter: boolean,
  companyNameDropdownFilterVisible: boolean,
  companyNameFilter: boolean,
  companySearchText: string,
  // Will be empty if it has not loaded yet.
  customerTypes: DataSourceItemObject[],
  customerTypeFilteredValue: string[],
  filteredInfo: IFilteredInfo,
  filteredCompanies: ICompany[],
  loading: boolean,
  filtered: boolean,
}

export interface ILocalProps {
  logoutFunc: Function,
  token: string,
}

class Companies extends React.Component<ILocalProps, ILocalState> {
  public constructor(props: ILocalProps) {
    super(props);

    this.state = {
      companies: [],
      companyIDDropdownFilterVisible: false,
      companyIDFilter: false,
      companyNameDropdownFilterVisible: false,
      companyNameFilter: false,
      companySearchText: '',
      customerTypeFilteredValue: [],
      customerTypes: [],
      filtered: false,
      filteredCompanies: [],
      filteredInfo: {companyType: []},
      loading: true,
    }
  }

  public async componentDidMount() {
    // We don't sequentially await, we kick off both requests concurrently.
    const allPromises = [
      getCompanies(this.props.token).then(companies => {
        this.setState({
          companies,
          filteredCompanies: companies,
        });
      }),
      getCustomerTypes(this.props.token).then(customerTypes => {
        this.setState({
          customerTypes,
        });
      })
    ];
    await Promise.all(allPromises).catch(() => {
      this.props.logoutFunc();
      notification.error({ message: 'Please log in' });
    });

    // All done.
    this.setState({
      loading: false,
    });
  }

  // TODO: There's no need to resync every single company once something
  // changes about one company. Update this function to update the state
  // of the one company that was modified.
  public updateCompanies = async () => {
    this.setState({loading: true});
    const companies = await getCompanies(this.props.token);
    this.setState({
      companies,
      loading: false,
    });
  }

  // Our column search autocomplete components call back to this function for information
  // on how to filter the information when a request is made.
  public onSearch = (filterType: string, searchText: any) => {
    let companies: ICompany[] = [];
    let nameFilter = false;
    let idFilter = false;

    switch (filterType) {
        case 'companyName':
          companies = searchText ? this.state.companies.filter((record: ICompany) => record.companyName === searchText) : this.state.companies;
          if (searchText) {
            nameFilter = true;
          }
          break;
        case 'companyProfileID':
          companies = searchText ? this.state.companies.filter((record: ICompany) => record.companyProfileID.toString() === searchText) : this.state.companies;
          if (searchText) {
            idFilter = true;
          }
        break;
    }
    this.setState({
      companyIDDropdownFilterVisible: false,
      companyIDFilter: idFilter,
      companyNameDropdownFilterVisible: false,
      companyNameFilter: nameFilter,
      filteredCompanies: companies,
    });
}

  public filterCompanyTypes = (value: string, record: ICompany) => {
    return record.companyType === value;
  };

  public render() {
    const columns: Array<ColumnProps<ICompany>> = [{
      dataIndex: 'companyProfileID',
      filterDropdown: (
        <SimpleColumnSearch
          inputCss={{width: '100px'}}
          onChange={this.onSearch.bind(this, 'companyProfileID')}
        />
      ),
      filterDropdownVisible: this.state.companyIDDropdownFilterVisible,
      filterIcon: <Icon type="filter" className={this.state.companyIDFilter ? 'filterActive' : 'filterNotActive'}/>,
      onFilterDropdownVisibleChange: (visible: boolean) => {
        this.setState({
          companyIDDropdownFilterVisible: visible,
        });
      },
      title: 'ID',
    }, {
      dataIndex: 'companyName',
      filterDropdown: (
        <div className="custom-filter-dropdown">
          <ColumnSearch
            data={this.state.filteredCompanies}
            onChange={this.onSearch.bind(this, 'companyName')}
            placeholder="Search by Name"
            columnName="companyName"
          />
        </div>
      ),
      filterDropdownVisible: this.state.companyNameDropdownFilterVisible,
      filterIcon: <Icon type="filter" className={this.state.companyNameFilter ? 'filterActive' : 'filterNotActive'}/>,
      onFilterDropdownVisibleChange: (visible: boolean) => {
        this.setState({
          companyNameDropdownFilterVisible: visible,
        });
      },
      title: 'Name',
    }, {
      dataIndex: 'companyType',
      // Holds the user-selected string values we wish to filter on
      filteredValue: this.state.filteredInfo.companyType,
      // Available company types to select from
      filters: this.state.customerTypes,
      onFilter: this.filterCompanyTypes,
      title: 'Type',
    }, {
      key: 'action',
      render: (text: string, record: ICompany) => (
        <span>
            <Dropdown overlay={
              <Menu>
                <Menu.Item>
                  <NavLink to={`/reporting/chargeHistory/${record.companyProfileID}`}>View Charge History</NavLink>
                </Menu.Item>
                <Menu.Item>
                  <NavLink to={`/companies/${record.companyProfileID}/plan`}>View Plan Details</NavLink>
                </Menu.Item>
                <Menu.Item>
                  <span onClick={() => deletePaymentInstrument(record.companyProfileID, record.companyName)}>
                    Clear Stored Payment Method
                  </span>
                </Menu.Item>
                <TerminationMenuItem
                  companyID={record.companyProfileID}
                  terminationRequestID={record.terminationRequestID}
                  onChange={this.updateCompanies}
                  companyName={record.companyName}
                />
              </Menu>
            }>
              <a className="ant-dropdown-link" onClick={e => e.preventDefault()}>
                Choose <Icon type="down"/>
              </a>
            </Dropdown>
          </span>
      ),
      title: 'Action',
    }];
    return (
      <div>
        <Table
          columns={columns}
          rowKey="companyProfileID"
          dataSource={this.state.filteredCompanies}
          loading={this.state.loading}
          onChange={this.handleChange}
        />
      </div>
    );
  }

  private handleChange = (pagination: PaginationConfig, filters: any, sorter: object) => {
    this.setState({
      filteredInfo: filters,
    });
  };
}

export default Companies;
